import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  alertTeamsApi,
  alertMutesApi,
  heartbeatSourcesApi,
  type AlertCoverage,
} from '@/api/alerts'

const errMsg = (e: unknown, fallback: string) =>
  (e as any)?.response?.data?.detail || (e as Error)?.message || fallback

export function useAlertTeams() {
  return useQuery({
    queryKey: ['alert-teams'],
    queryFn: () => alertTeamsApi.list(),
    staleTime: 60_000,
  })
}

export function useAlertMutes() {
  return useQuery({
    queryKey: ['alert-mutes'],
    queryFn: () => alertMutesApi.list(),
    staleTime: 30_000,
  })
}

export function useCreateMute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { scope_key: string; hours: number }) => alertMutesApi.create(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alert-mutes'] })
      notifications.show({ color: 'teal', message: 'Silenciado.' })
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Erro', message: errMsg(e, 'Falha ao silenciar.') }),
  })
}

export function useRemoveMute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => alertMutesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-mutes'] }),
  })
}

export function useTeamMutations() {
  const qc = useQueryClient()
  const inv = () => qc.invalidateQueries({ queryKey: ['alert-teams'] })
  const ok = (m: string) => notifications.show({ color: 'teal', message: m })
  const fail = (e: unknown) =>
    notifications.show({ color: 'red', title: 'Erro', message: errMsg(e, 'Falha.') })

  return {
    create: useMutation({
      mutationFn: (p: { name: string; description?: string }) => alertTeamsApi.create(p),
      onSuccess: () => {
        inv()
        ok('Time criado.')
      },
      onError: fail,
    }),
    deactivate: useMutation({
      mutationFn: (id: number) => alertTeamsApi.deactivate(id),
      onSuccess: () => {
        inv()
        ok('Time desativado.')
      },
      onError: fail,
    }),
    addMember: useMutation({
      mutationFn: (v: { id: number; userId: number }) =>
        alertTeamsApi.addMember(v.id, v.userId),
      onSuccess: () => {
        inv()
        ok('Membro adicionado.')
      },
      onError: fail,
    }),
    setCoverage: useMutation({
      mutationFn: (v: { id: number; c: AlertCoverage }) =>
        alertTeamsApi.setCoverage(v.id, v.c),
      onSuccess: () => {
        inv()
        ok('Cobertura salva.')
      },
      onError: fail,
    }),
  }
}


// ---------------------------------------------------------------------------
// Fontes de heartbeat (TASK-10040)
//
// `staleTime` curto de propósito: a coluna de sinal é estado operacional, não
// cadastro — lista de 5 min mostraria "em dia" para uma fonte que calou há 4.
// ---------------------------------------------------------------------------

export function useHeartbeatSources() {
  return useQuery({
    queryKey: ['heartbeat-sources'],
    queryFn: () => heartbeatSourcesApi.list(),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

export function useHeartbeatSourceMutations() {
  const qc = useQueryClient()
  const inv = () => qc.invalidateQueries({ queryKey: ['heartbeat-sources'] })
  const ok = (m: string) => notifications.show({ color: 'teal', message: m })
  const fail = (e: unknown) =>
    notifications.show({ color: 'red', title: 'Erro', message: errMsg(e, 'Falha.') })

  return {
    create: useMutation({
      mutationFn: (p: { source: string; stream?: string; window_seconds: number }) =>
        heartbeatSourcesApi.create(p),
      onSuccess: () => {
        inv()
        ok('Fonte registrada. O vigia passa a cobrar sinal dela.')
      },
      onError: fail,
    }),
    update: useMutation({
      mutationFn: (p: {
        id: number
        window_seconds?: number
        is_active?: boolean
      }) => heartbeatSourcesApi.update(p.id, {
        window_seconds: p.window_seconds,
        is_active: p.is_active,
      }),
      onSuccess: () => {
        inv()
        ok('Fonte atualizada.')
      },
      onError: fail,
    }),
    remove: useMutation({
      mutationFn: (id: number) => heartbeatSourcesApi.remove(id),
      onSuccess: () => {
        inv()
        ok('Fonte removida do registro.')
      },
      onError: fail,
    }),
  }
}
